import dayjs from 'dayjs';
import { fromPromise } from 'xstate';

import { ApiRespCode } from '@webapp/common/lib/api';
import { isPreview } from '@webapp/common/lib/const';
import {
    clearSurveyCompleteCookie,
    clearSurveyTokenCookie,
    getCookie,
    setCookieWithSubdomains,
    setSurveyPromocode,
    setSurveyTokenCookie,
} from '@webapp/common/lib/cookies';
import { GOALS, reachGoal } from '@webapp/common/lib/metrics';
import { appendSearchParams } from '@webapp/common/lib/search-params';
import { sleep } from '@webapp/common/lib/sleep';
import { LocalStorage } from '@webapp/common/lib/storage';
import { isEmbedded } from '@webapp/common/lib/ua';
import { createSubdomainUrl } from '@webapp/common/lib/utils';
import type { PageModel, QuestionLogicModel, QuestionModel, SurveyStore } from '@webapp/common/resources/entities';
import { BundleType } from '@webapp/common/resources/entities';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import { CompletePageType, QuestionType } from '@webapp/common/resources/survey';

import { getApiClass } from '../api/queries-api';

import {
    cancelPendingDetails,
    isQuotaSessionActive,
    saveAnswersOffline,
    setQuotaSessionActive,
    surveyApi,
} from './api';
import { AudioState } from './audio-machine';
import { getSearchParams } from './get-search-params';
import { withRandomPages } from './guards';
import { mapInfo } from './helpers/normalize';
import { isOfflineMode } from './is-offline-mode';
import type { SavedCompleteSurvey } from './lib';
import {
    clearAllTimers,
    getLkToken,
    loadCompleteSurvey,
    preloadImages,
    saveCompleteSurvey,
    setTimersPersistence,
} from './lib';
import { selectCurrentBundle, selectNextBundle } from './selectors';
import { clearTtId } from './tt-id';

const SKIP_SEND_ANSWERS_QUESTION_TYPES = [QuestionType.VIDEO_TEST, QuestionType.FIVE_SEC_TEST];

export const fetchContent = fromPromise<
    {
        pages: Array<PageModel>;
        questions: Array<QuestionModel>;
        withRandom: boolean;
        linkParamsLogic: Array<QuestionLogicModel>;
        linkParams: Record<string, string>;
        extendedLogic: Array<QuestionLogicModel>;
        quotaExhausted: boolean;
        hasSurveyQuota: boolean;
        closedQuotaLogicIds: Array<number>;
    },
    { context: SurveyStore }
>(async ({ input }) => {
    const {
        pages,
        questions,
        linkParamsLogic,
        linkParams,
        extendedLogic,
        quotaExhausted,
        hasSurveyQuota,
        closedQuotaLogicIds,
    } = await surveyApi.getSurveyData(input.context);
    await preloadImages(questions);

    return {
        pages,
        questions,
        withRandom: withRandomPages(pages),
        linkParamsLogic,
        linkParams,
        extendedLogic,
        quotaExhausted,
        hasSurveyQuota,
        closedQuotaLogicIds,
    };
});

// Перезагрузка страницы (а не первый заход): только в этом случае прошлая
// квота-сессия точно принадлежит этому же респонденту и её можно освободить
// перед новым стартом.
const isPageReload = (): boolean => {
    if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
        return false;
    }

    const [navigation] = performance.getEntriesByType('navigation');

    return (navigation as PerformanceNavigationTiming | undefined)?.type === 'reload';
};

export const bootstrap = async ({
    domain,
    token,
}: {
    domain: string;
    token: string;
}): Promise<{
    domain: string;
    code: number;
    token: string;
    startDatetime: dayjs.Dayjs;
    info: InfoModel | null;
    agreementText: string | null;
    qp: Record<string, string>;
    quotaFilled: boolean;
    complete: boolean;
    promo_code: string;
}> => {
    const now = dayjs();
    const lktoken = getLkToken();
    const params = getSearchParams();
    const searchRaw = window.location.search.substring(1);

    try {
        const cookieValue = getCookie('is_canary');
        const lcValue = LocalStorage.get('survey_is_canary');
        if (cookieValue === null && lcValue === null) {
            await surveyApi.checkCanary(domain);
        }
        if (!cookieValue && lcValue) {
            setCookieWithSubdomains('is_canary', 'always', {
                expires: dayjs().add(1, 'day').toDate(),
            });
            // На localhost кука с Domain может не сохраниться — не уходим в бесконечный reload
            if (getCookie('is_canary')) {
                window.location.reload();
                await sleep(3000);
            }
        }
    } catch (err) {
        console.warn('Canary err:', err);
    }

    // Перезагрузка страницы: освобождаем бронь прошлой сессии до нового
    // резерва. Unload-запрос (keepalive) мог не дойти при выгрузке, и тогда
    // за респондентом висели бы два места: старое до TTL и новое.
    // Шлём только если в этой вкладке стартовала квота-сессия (флаг ставит
    // QuotaMachine): опросам без квот release не нужен вовсе, а с
    // «до-парольным» токеном он к тому же отваливается 500 — release не в
    // whitelist парольной авторизации (FRONT-2A8M).
    if (token && isPageReload() && isQuotaSessionActive(domain)) {
        try {
            surveyApi.setToken(token);
            await surveyApi.releaseQuotaSession();
        } catch (e) {
            console.warn('[quota] release on reload failed', e);
        } finally {
            // Освобождение best-effort: если запрос не дошёл, слот вернётся по
            // TTL. Флаг снимаем в любом случае, чтобы не повторять запрос на каждый F5.
            setQuotaSessionActive(domain, false);
        }
    }

    const data = await surveyApi.startSurvey(domain, token, params, searchRaw, lktoken);

    const { code, rows } = data;

    const info = rows ? mapInfo(data) : null;

    // Без сохранения незавершённых перезагрузка страницы = новый ответ,
    // поэтому таймеры не должны переживать перезагрузку (см. lib.ts).
    setTimersPersistence(Boolean(info?.savingNotComplete));

    token = code === ApiRespCode.OK_CODE ? data.token : null;
    if (data.complete) {
        token = data.newToken;
    }
    const search = new URLSearchParams(window.location.search);
    Object.keys(data.queryParams ?? {}).forEach((key) => {
        search.append(key, data.queryParams[key]);
    });
    const qp = [...search.keys()].reduce((acc, key) => {
        acc[key] = search.get(key);
        return acc;
    }, {} as Record<string, string>);
    return {
        domain,
        agreementText: data.agreementText || null,
        code,
        token,
        info,
        startDatetime: now,
        qp,
        // Общая квота опроса набрана (start/response вернул quotaFilled):
        // показываем страницу завершения с текстом quotaFilledText.
        quotaFilled: Boolean(data.quotaFilled),
        // start/response с токеном уже завершённого ответа: показываем страницу
        // завершения, а не начинаем опрос заново. promo_code бэкенд отдаёт
        // вместе с complete, чтобы промокод не терялся после перезагрузки.
        complete: Boolean(data.complete),
        promo_code: data.promo_code || '',
    };
};

export const checkVkAuth = async (authdata: string, domain: string, token: string): Promise<{ token: string }> => ({
    token: await surveyApi.checkVkAuth(domain, authdata, token, window.location.search.slice(1)),
});

const stop = async (domain: string, useSaved: boolean, surveyId: number): Promise<SavedCompleteSurvey> => {
    let promo_code, questions, testScore, quotaDisqual;

    if (useSaved) {
        ({ promo_code, questions, testScore, quotaDisqual } = loadCompleteSurvey(domain));
    } else {
        ({ promo_code, questions, testScore, quotaDisqual } = await surveyApi.stopSurvey());
        saveCompleteSurvey(domain, { promo_code, questions, testScore, quotaDisqual });
        parent?.postMessage('finish', '*');
        parent?.postMessage(JSON.stringify({ type: 'finish', id: surveyId }), '*');
    }

    return { promo_code, questions, testScore, quotaDisqual };
};

const WEBSITE_PROTO = 'https://';

export const finishSurveyExpectedErrors = new Set([
    ApiRespCode.ERR_END_SURVEY,
    ApiRespCode.ERR_DELETED_RESPONSE,
    ApiRespCode.ERR_ANSWERED_RESPONSE,
]);

export const fillAbTestingQuestions = (questions) => {
    questions.forEach((q) => {
        const qData = q.getSnapshot().context;

        if (qData.type === QuestionType.AB_TESTING) {
            const answers = qData.answersActors.map((a) => a.getSnapshot());
            const answerIndex = answers.findIndex((item) => item.context.id === qData.selectedAnswerId);

            answers[answerIndex]?.context.response.send({
                type: 'CHANGE',
                value: true,
                questionId: qData.id,
            });
        }
    });
};

const prepareAnswersForSend = (questions) => {
    return questions
        .filter((q) => {
            const { dirty, type } = q.getSnapshot().context;

            if (SKIP_SEND_ANSWERS_QUESTION_TYPES.includes(type)) {
                return false;
            }

            return dirty;
        })
        .map((question) => getApiClass(question.getSnapshot().context.type).query(question));
};

export const finishSurvey = async (ctx: SurveyStore) => {
    const offline = isOfflineMode();
    const {
        main: { completePage, completeSurvey, completeSurveyUrl, completeWebsite },
        other: { copyUrlParams },
        // FIXME: check need or not
    } = JSON.parse(JSON.stringify(ctx.info.params));
    const domain = ctx.domain;

    // Сбрасываем сохранённые таймеры сразу и синхронно: отложенная очистка
    // через setTimeout ниже не успевает выполниться, если пользователь закрыл
    // вкладку (мобильные) или упал запрос завершения — и таймер не обнулялся
    // при следующем прохождении.
    clearAllTimers(ctx.domain, ctx.info.id);

    fillAbTestingQuestions(ctx.questions);

    const answers = prepareAnswersForSend(ctx.questions);

    // Финальная отправка: частичные ответы больше не нужны — гасим таймер
    // add/detail и чистим очередь до запроса. Иначе, пока add/all_detail
    // в полёте (медленный бэкенд), таймер срабатывает и отправляет add/detail
    // с последними ответами поверх финальных (#1152).
    cancelPendingDetails();

    if (offline) {
        await saveAnswersOffline(answers, ctx);
    } else {
        // FIXME: check need or not
        await surveyApi.sendAllAnswers(JSON.parse(JSON.stringify(answers)));
    }
    LocalStorage.remove('new_question_details');
    let promo_code: string, questions: Array<any>, testScore: number;
    let quotaDisqual = false;
    if (!offline) {
        ({ promo_code, questions, testScore, quotaDisqual } = await stop(domain, false, ctx.info.id));
    }

    // Сохраняем промокод сразу и синхронно: setTimeout ниже может не успеть
    // выполниться, если вкладку закрыли/перезагрузили сразу после завершения.
    // По этой cookie страница завершения восстанавливает промокод при
    // повторном заходе (в т.ч. когда start/response вернул 70/77).
    if (promo_code) {
        setSurveyPromocode(promo_code);
    }

    // Цель «ответ на опрос»: только после успешного stop на бэкенде.
    // Промежуточные сохранения (add/detail, cache) сюда не попадают.
    // Дисквалифицированный по квоте ответ конверсией не считаем.
    const goalReached = new Promise<void>((resolve) => {
        if (offline || isPreview || quotaDisqual || !ctx.info?.id) {
            resolve();
            return;
        }

        const goal = ctx.info.savingNotComplete ? GOALS.surveyAnswerPartial : GOALS.surveyAnswer;

        reachGoal(goal, { survey_id: ctx.info.id }, resolve);
    });

    if (ctx.timerRef) {
        ctx.timerRef.send({ type: 'STOP' });
    }
    ctx.questions.forEach((q) => {
        if (q.getSnapshot().context.timer) {
            q.getSnapshot().context.timer.send({ type: 'STOP' });
        }
    });
    const urlParams = new URLSearchParams(window.location.search);
    setTimeout(
        () => {
            if (ctx.info.params.other.disallowRefilling) {
                LocalStorage.set(`survey_${ctx.domain}_question_answers`, JSON.stringify(ctx.questionAnswers));
                LocalStorage.set(
                    `survey_${ctx.domain}_link_params`,
                    JSON.stringify({ ...ctx.linkParams, ...ctx.queryParams }),
                );
                LocalStorage.remove(`new_answers__${ctx.domain}`);
                LocalStorage.remove('new_question_details');
            } else {
                clearSurveyCompleteCookie();
                clearSurveyTokenCookie();
                LocalStorage.clearWithExceptions(['lang']);
            }
            if (ctx.info.savingNotComplete) {
                surveyApi.deleteCachedData();
                clearTtId();
            }
            if (urlParams.get('source') === 'email' && urlParams.has('id')) {
                LocalStorage.set(`survey_${ctx.domain}_question_answers`, JSON.stringify(ctx.questionAnswers));
                LocalStorage.set(
                    `survey_${ctx.domain}_link_params`,
                    JSON.stringify({ ...ctx.linkParams, ...ctx.queryParams }),
                );
            }
        },
        ctx.info.params.main.completePage === CompletePageType.TEST_RESULTS ? 3_000 : 0,
    );
    if (ctx.audioRef?.getSnapshot().value === AudioState.STARTED) {
        ctx.audioRef.send({ type: 'STOP' });
        while (ctx.audioRef?.getSnapshot().value !== AudioState.STOPPED) {
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }
    const nextBundle = selectNextBundle(ctx);
    const currentBundle = nextBundle || selectCurrentBundle(ctx);

    if (!currentBundle) {
        // Пустой набор бандлов (все вопросы отфильтрованы логикой или вопросов нет):
        // раньше падало на currentBundle.type (FRONT-1ZPB), а с guard'ом пропускался
        // и редирект completePage (FRONT-2A9Z). Bundle-ветку пропускаем, завершение
        // по настройкам опроса ниже выполняем всегда.
        console.error('finish: current bundle not found', {
            index: ctx.currentBundleIndex,
            bundles: ctx.bundles.length,
            pages: ctx.pages.length,
            questions: ctx.questions.length,
            domain: ctx.domain,
        });
    }

    try {
        switch (currentBundle?.type) {
            case BundleType.TEXT:
                break;
            case BundleType.REDIRECT_URL:
            case BundleType.REDIRECT_SURVEY:
                if (!currentBundle.redirect) {
                    console.error('EMPTY REDIRECT URL');
                }

                const bundleCopyParams = currentBundle.copyUrlParams || copyUrlParams;
                let url = appendSearchParams(
                    String(currentBundle.redirect),
                    bundleCopyParams ? window.location.search : '',
                );

                if (!url.startsWith(WEBSITE_PROTO)) {
                    url = WEBSITE_PROTO + url;
                }

                if (url === WEBSITE_PROTO) throw new Error('complete website not specified');

                // Редирект — после callback цели (или секундного таймаута),
                // иначе Метрика не успевает отправить событие.
                await goalReached;
                setTimeout(() => {
                    if (isEmbedded) {
                        window.open(url);
                    } else {
                        window.location.href = url;
                    }
                }, 100);
                return {};
            case BundleType.QUESTIONS:
                break;
            default:
                // if (getSurveyCompleteCookie()) {
                //     void self.doStopOrLoadComplete(true);
                // }

                break;
        }
    } catch (e) {
        console.error(e);
    }

    try {
        switch (completePage) {
            case CompletePageType.SUCCESS_TEXT:
                break;
            case CompletePageType.REDIRECT_TO_WEBSITE:
                let url = appendSearchParams(completeWebsite, copyUrlParams ? window.location.search : '');

                if (!url.startsWith(WEBSITE_PROTO)) {
                    url = WEBSITE_PROTO + url;
                }

                if (url === WEBSITE_PROTO) throw new Error('complete website not specified');

                await goalReached;
                setTimeout(() => {
                    if (isEmbedded) {
                        window.open(url);
                    } else {
                        window.location.href = url;
                    }
                }, 100);
                await sleep(10_000);
                return {};
            case CompletePageType.TO_SURVEY:
                if (!completeSurvey || !completeSurveyUrl) {
                    // Целевой опрос не выбран или уже удалён — не ломаем завершение,
                    // показываем стандартную страницу благодарности (FRONT-2AE0).
                    console.warn('complete survey not specified, fallback to success page');
                    break;
                }

                await goalReached;
                window.location.href = appendSearchParams(
                    createSubdomainUrl(completeSurveyUrl),
                    copyUrlParams ? window.location.search : '',
                );
                await sleep(10_000);
                return {};
            case CompletePageType.SURVEY_RESULTS:
                break;
            case CompletePageType.TEST_RESULTS:
                // if (getSurveyCompleteCookie()) {
                //     void self.doStopOrLoadComplete(true);
                // }

                break;
        }
    } catch (e) {
        console.error(e);
    }

    return { promo_code, questions, testScore, quotaDisqual };
};

export const disqualSurvey = async (ctx: SurveyStore) => {
    const offline = isOfflineMode();
    const {
        other: { copyUrlParams },
        // FIXME: check need or not
    } = JSON.parse(JSON.stringify(ctx.info.params));

    // Дисквал завершает прохождение: сервер (disqual/response) освободит
    // брони квот сразу, не дожидаясь TTL. Гасим heartbeat заранее, чтобы он
    // не продлевал сессию и не прислал QUOTA_EXPIRED во время завершения.
    ctx.quotaRef?.send({ type: 'STOP_QUOTAS' });
    setQuotaSessionActive(ctx.domain, false);

    // Синхронный сброс сохранённых таймеров — см. комментарий в finishSurvey.
    clearAllTimers(ctx.domain, ctx.info.id);

    fillAbTestingQuestions(ctx.questions);

    const answers = prepareAnswersForSend(ctx.questions);

    // Дисквал — тоже финализация (см. finishSurvey): частичный add/detail
    // не должен уйти после финального add/all_detail (#1152).
    cancelPendingDetails();

    if (offline) {
        await saveAnswersOffline(answers, ctx);
    } else {
        // FIXME: check need or not
        await surveyApi.sendAllAnswers(JSON.parse(JSON.stringify(answers)));
    }

    const nextBundle = selectNextBundle(ctx);
    const currentBundle = nextBundle || selectCurrentBundle(ctx);

    if (!offline) {
        await surveyApi.disqualSurvey();
    }
    parent?.postMessage('disqual', '*');

    if (ctx.audioRef?.getSnapshot().value === AudioState.STARTED) {
        ctx.audioRef.send({ type: 'STOP' });
        while (ctx.audioRef?.getSnapshot().value !== AudioState.STOPPED) {
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (ctx.timerRef) {
        ctx.timerRef.send({ type: 'STOP' });
    }
    ctx.questions.forEach((q) => {
        if (q.getSnapshot().context.timer) {
            q.getSnapshot().context.timer.send({ type: 'STOP' });
        }
    });
    setTimeout(
        () => {
            if (ctx.info.params.other.disallowRefilling) {
                LocalStorage.remove(`new_answers__${ctx.domain}`);
                LocalStorage.remove('new_question_details');
            } else {
                clearSurveyCompleteCookie();
                clearSurveyTokenCookie();
                LocalStorage.clearWithExceptions(['lang']);
            }
            if (ctx.info.savingNotComplete) {
                surveyApi.deleteCachedData();
                clearTtId();
            }
        },
        ctx.info.params.main.completePage === CompletePageType.TEST_RESULTS ? 3_000 : 0,
    );

    if (!currentBundle) {
        // Дисквал с отсутствующим бандлом: раньше падало на currentBundle.type
        // (FRONT-1ZPB), switch с редиректом молча пропускался. Сигнал сохраняем.
        console.error('disqual: current bundle not found', {
            index: ctx.currentBundleIndex,
            bundles: ctx.bundles.length,
            pages: ctx.pages.length,
            questions: ctx.questions.length,
            domain: ctx.domain,
        });
        return {};
    }

    try {
        switch (currentBundle.type) {
            case BundleType.TEXT:
                break;
            case BundleType.REDIRECT_URL:
            case BundleType.REDIRECT_SURVEY:
                if (!currentBundle.redirect) {
                    console.error('EMPTY REDIRECT URL');
                }
                const bundleCopyParams = currentBundle.copyUrlParams || copyUrlParams;
                let url = appendSearchParams(
                    String(currentBundle.redirect),
                    bundleCopyParams ? window.location.search : '',
                );

                if (!url.startsWith(WEBSITE_PROTO)) {
                    url = WEBSITE_PROTO + url;
                }

                if (url === WEBSITE_PROTO) throw new Error('complete website not specified');

                setTimeout(() => {
                    if (isEmbedded) {
                        window.open(url);
                    } else {
                        window.location.href = url;
                    }
                }, 100);
                await sleep(10_000);
                return {};
            case BundleType.QUESTIONS:
                break;
            default:
                // if (getSurveyCompleteCookie()) {
                //     void self.doStopOrLoadComplete(true);
                // }

                break;
        }
    } catch (e) {
        console.error(e);
    }

    return {};
};

export const isFinishTypeResults = ({ context }: { context: SurveyStore }) => {
    const {
        main: { completePage },
    } = context.info.params;

    const currentBundle = selectCurrentBundle(context);

    if (currentBundle && currentBundle.type && currentBundle.type !== BundleType.QUESTIONS) {
        return false;
    }
    if (completePage === CompletePageType.SURVEY_RESULTS) {
        return true;
    }
};

export const checkPwdAuth = async (individualPasswords: boolean, password: string) => {
    const token = individualPasswords
        ? await surveyApi.checkIndividualPassword(password)
        : await surveyApi.checkPassword(password);

    if (typeof token === 'string') {
        setSurveyTokenCookie(token);
    }

    return { token, password };
};
